import { computed, reactive } from 'vue';
import {
    CustomizerFacetViewModel,
    CustomizerTabType, FacetOptions,
    MaterialAndColorCustomizerModel, MaterialAndColorVariant, MaterialModel,
} from '@/types/productServerApi';
import useCustomizer from '@/shared/product/customizer/useCustomizer';
import { sortBy, includes, without } from 'lodash';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';

export enum MaterialAndColorSearchFacetType {
    Colors = 'colors',
    Materials = 'primaryMaterials',
    Unknown = '',
}

export interface MaterialAndColorSearchFacetFilter {
    MaterialAndColorSearchFacetType: string[];
}

const state = reactive({
    pending: false,
    result: {} as MaterialAndColorCustomizerModel,
    filters: {} as MaterialAndColorSearchFacetFilter,
});

const facetsPriority = [MaterialAndColorSearchFacetType.Colors, MaterialAndColorSearchFacetType.Materials, MaterialAndColorSearchFacetType.Unknown];

export default function useMaterialAndColor() {
    const { activeCustomizerOverlayType, activeCustomizerOverlayId, clearActiveCustomizerOverlay, getMaterialAndColorCustomizer } = useCustomizer();
    const { variantId } = useProductDetailData();
    const labels = useBlockLabels();

    const isActive = computed<boolean>(() => activeCustomizerOverlayType.value === CustomizerTabType.MaterialAndColor);
    const isPending = computed<boolean>(() => state.pending);
    const result = computed<MaterialAndColorCustomizerModel>(() => state.result);
    const facets = computed<CustomizerFacetViewModel[]>(() => {
        return sortBy(state.result.facets, facet => facetsPriority.indexOf((facet?.id as MaterialAndColorSearchFacetType) ?? MaterialAndColorSearchFacetType.Unknown)) ?? [] as CustomizerFacetViewModel[];
    });
    const filters = computed<MaterialAndColorSearchFacetFilter>(() => state.filters);
    const materials = computed<MaterialModel[]>(() => state.result?.materials ?? [] as MaterialModel[]);
    const filteredMaterials = computed<MaterialModel[]>(() => {
        let filteredMaterials = state.result?.materials ?? [] as MaterialModel[];
        const materialFilters = filters.value[MaterialAndColorSearchFacetType.Materials];
        if (materialFilters && materialFilters.length) {
            filteredMaterials = filteredMaterials.filter((material) => {
                return includes(materialFilters, material.materialTypeNameValue);
            });
        }
        return filteredMaterials.filter((material) => getFilteredVariants(material.variantWithPrices).length > 0);
    });

    const getFilteredVariants = (variants: MaterialAndColorVariant[]) => {
        const colorFilters = filters.value[MaterialAndColorSearchFacetType.Colors] ?? [];
        if (colorFilters.length === 0) {
            return variants;
        }
        return variants.filter((variant) => {
            return colorFilters.indexOf(variant.colorValue) > -1;
        });
    };

    const isClearedResult = computed<boolean>(() => Object.keys(result.value).length < 1);

    const getResultCountForMaterialTypeValue = (value?: string) => {
        let totalCount = 0;
        const materialsOfType = value ? materials.value.filter(material => material.materialTypeNameValue === value) : materials.value;
        materialsOfType.forEach(material => {
            totalCount += getFilteredVariants(material.variantWithPrices).length;
        });
        return totalCount;
    };

    const getMaterialTabsFromOptions = (options: FacetOptions[]) => {
        const getTabTitle = (title, value?): string => {
            const count = getResultCountForMaterialTypeValue(value);
            return `${title} (${count})`;
        };

        const defaultTab = getTabTitle(labels.overlayAllMaterialsTabTitle);
        const optionTabs = options.map((option) => getTabTitle(option.title, option.value));

        return [defaultTab, ...optionTabs];
    };

    const getSelectedPriceFromVariants = (variants: MaterialAndColorVariant[]) => {
        const selectedVariant = variants.find((variant) => variant.sku === variantId.value);
        return selectedVariant?.price ?? variants[0].price;
    };

    const getSelectedSkuFromVariants = (variants: MaterialAndColorVariant[]) => {
        const selectedVariant = variants.find((variant) => variant.sku === variantId.value);
        return selectedVariant?.sku ?? variants[0].sku;
    };

    const search = async() => {
        const result = await getMaterialAndColorCustomizer(activeCustomizerOverlayId.value);
        return state.result = result;
    };

    const clearResult = () => {
        state.result = {} as MaterialAndColorCustomizerModel;
    };

    const clearFilters = () => {
        state.filters = {} as MaterialAndColorSearchFacetFilter;
    };

    const clearSearch = () => {
        clearFilters();
        clearResult();
    };

    const selectMaterial = () => {
        clearActiveCustomizerOverlay();
    };

    const setPending = (pending: boolean) => {
        state.pending = pending;
    };

    const getIsSelectedColor = (color?: string) => {
        if (!color) {
            return false;
        }
        let colorsFilter = filters.value[MaterialAndColorSearchFacetType.Colors];
        colorsFilter = colorsFilter ?? [];
        return includes(colorsFilter, color);
    };

    const toggleSearchFacetColorsFilter = (color?: string) => {
        if (!color) {
            return;
        }
        let colorsFilter = filters.value[MaterialAndColorSearchFacetType.Colors];
        colorsFilter = colorsFilter ?? [];
        if (getIsSelectedColor(color)) {
            colorsFilter = without(colorsFilter, color);
        } else {
            colorsFilter.push(color);
        }
        filters.value[MaterialAndColorSearchFacetType.Colors] = colorsFilter;
    };

    const setSearchFacetMaterialsFilter = (material: string) => {
        filters.value[MaterialAndColorSearchFacetType.Materials] = [material];
    };

    const clearSearchFacetMaterialsFilter = () => {
        state.filters[MaterialAndColorSearchFacetType.Materials] = [];
    };

    const getMaterialFacetValueByOptionIndex = (idx: number) => {
        const materialFacet = facets.value.find(facet => facet.id === MaterialAndColorSearchFacetType.Materials);
        if (!materialFacet) {
            return undefined;
        }
        const materialFacetOption = materialFacet.options[idx];
        return materialFacetOption?.value;
    };

    const getMaterialFacetOptonIndexByValue = (value?: string) => {
        const materialFacet = facets.value.find(facet => facet.id === MaterialAndColorSearchFacetType.Materials);
        if (!materialFacet || !value) {
            return 0;
        }
        const materialFacetOptionIndex = materialFacet.options.findIndex((option) => option.value === value);
        return materialFacetOptionIndex + 1;
    };

    const materialFacetOptionsSelectedIndex = computed(() => {
        const materialsFilter = filters.value[MaterialAndColorSearchFacetType.Materials];
        if (!materialsFilter) {
            return 0;
        }
        const materialsFilterValue = materialsFilter[0];
        return getMaterialFacetOptonIndexByValue(materialsFilterValue);
    });
    const setSearchFacetMaterialsFilterByIndex = (idx: number) => {
        if (idx < 1) {
            clearSearchFacetMaterialsFilter();
        } else {
            const relativeIndex = idx - 1;
            const value = getMaterialFacetValueByOptionIndex(relativeIndex);
            value && setSearchFacetMaterialsFilter(value);
        }
    };

    return {
        isActive,
        isPending,
        result,
        facets,
        filteredMaterials,
        isClearedResult,
        filters,
        materialFacetOptionsSelectedIndex,
        setPending,
        search,
        clearSearch,
        selectMaterial,
        getMaterialTabsFromOptions,
        getSelectedPriceFromVariants,
        getSelectedSkuFromVariants,
        getFilteredVariants,
        getIsSelectedColor,
        toggleSearchFacetColorsFilter,
        setSearchFacetMaterialsFilterByIndex,
        clearSearchFacetMaterialsFilter,
    };
}
